import { useReducer } from "react";
import { useTranslation } from "react-i18next";

import socket from "@/utils/socket";
import { showToast } from "@/utils/toast";
import AcceptModal from "./Modal/AcceptModal";
import RejectModal from "./Modal/RejectModal";
import { OrderMode, OrderSource } from "@/enum";
import { SocketEvent } from "@/enum/socketEvent";
import { useAppContext } from "@/use-context/Context";
import { apiOrderAction } from "@/services/ServiceApi";
import { getCurrencyConfig, getShop } from "@/utils/auth";
import { showToastInvalid } from "./Message/showToastInvalid";
import { showToastSuccess } from "./Message/showToastSuccess";
import { orderType, productStatus, productType } from "@/config";
import { calculatePriceAfterDiscount, calPriceDiscount } from "@/helper";
import NotificationAlert from "@/pages/Chef/components/NotificationAlert";
import { convertToBaseAmount, formatPriceByCurrency } from "@/utils/helper";
import { ACTIONS, initialState, orderReducer } from "@/reducers/OrderReducer";

interface ListOrderFooterProps {
    orderObj: orderType[];
    tableId?: string;
    fetchOrderByTableId: () => void;
    resetSelectAll: () => void;
}

export default function ListOrderFooter({ tableId, fetchOrderByTableId, resetSelectAll }: ListOrderFooterProps) {
    const shop = getShop();
    const { t } = useTranslation();
    const getCurrency = getCurrencyConfig();
    const { listItems, setListItems, fetchProduct, addToCart, setAddToCart } = useAppContext();

    const tableName = localStorage.getItem("tableName");

    const [state, dispatch] = useReducer(orderReducer, initialState);

    const filteredOrders = addToCart.filter(order => order.isCheck);
    const filteredOrdersReject = addToCart.filter(order => order.isCheck && !order.isWaiter);

    const getFilteredData = (data: productType[], type: "default" | "new"): any[] => {
        return data
            .filter(product => product.quantity > 0)
            .flatMap(product => {
                const checkedAddOns = product.addons?.filter(addOn => addOn.isCheck) || [];

                const resultAddOnsItem = checkedAddOns.map(item => {
                    if (type === "default") {
                        return {
                            tableId: tableId,
                            businessId: shop ? shop._id : "",
                            detailId: product._id,
                            orderId: product.orderId,
                            tableName: tableName,
                            quantity: product.quantity,
                            name: item.name,
                            nameKH: item.nameKH,
                            // price: item.price,
                            instruction: "",
                            tracking: product.tracking,
                            status: product.status,
                            options: [],
                        };
                    } else {
                        return {
                            productId: item._id,
                            quantity: product.quantity,
                            instruction: product.instruction || "",
                        };
                    }
                });

                const filteredFoodOptions = product.options
                    ? product.options.map(optionItem =>
                        optionItem.values.filter(option => option.isCheck).map(option => option._id)
                    ).flat()
                    : [];

                const result = type === "default"
                    ? {
                        tableId: tableId,
                        businessId: shop ? shop._id : "",
                        detailId: product._id,
                        orderId: product.orderId,
                        tableName: tableName,
                        quantity: product.quantity,
                        name: product.name,
                        nameKH: product.nameKH,
                        // price: product.price,
                        instruction: "",
                        tracking: product.tracking,
                        status: product.status,
                        options: [],
                        discountPercent: product.discountPercent ? product.discountPercent : 0,
                        productStatus: product.requiresCooking ? productStatus.Accept : productStatus.Ready,
                        discountValue: calPriceDiscount(product.priceItem ? product.priceItem : product.price, product.discountPercent),
                    }
                    : {
                        productId: product._id,
                        instruction: product.instruction || "",
                        quantity: product.quantity,
                        optionValueIds: filteredFoodOptions,
                        discountPercent: product.discountPercent ? product.discountPercent : 0,
                        productStatus: product.requiresCooking ? productStatus.Accept : productStatus.Ready,
                        discountValue: calPriceDiscount(product.priceItem ? product.priceItem : product.price, product.discountPercent),
                    };

                return [result, ...resultAddOnsItem];
            });
    };

    const getFilteredDataReject = (data: productType[]): any[] => {
        return data
            .filter(product => product.quantity > 0)
            .flatMap(product => {
                const checkedAddOns = product.addons?.filter(addOn => addOn.isCheck) || [];

                const resultAddOnsItem = checkedAddOns.map(item => {
                    return {
                        detailId: product.detailId,
                        orderId: product.orderId,
                        productName: item.name,
                        productNameKH: item.nameKH,
                        tableId: tableId,
                        tableName: tableName,
                    };
                });

                const result = {
                    detailId: product.detailId,
                    orderId: product.orderId,
                    productName: product.name,
                    productNameKH: product.nameKH,
                    tableId: tableId,
                    tableName: tableName,
                }

                return [result, ...resultAddOnsItem];
            });
    };

    function calculateTotalPrice(addToCart: productType[]) {
        return addToCart
            .filter(item => (item.status !== 7 && item.status !== 2 && !item.isWaiter))
            .reduce((total, item) => {
                const qty = item.quantity ?? 0;

                if (item.discountPercent) {
                    const discountPrice = calculatePriceAfterDiscount(item.priceItem ? item.priceItem : item.price, item.discountPercent);
                    return total + (discountPrice * qty);
                } else {
                    const price = Number(item.totalPriceItem ? item.totalPriceItem : item.price ?? 0);
                    return total + (price * qty);
                }
            }, 0);
    }

    function calculateTotalPriceIsWaiter(addToCart: productType[]) {
        return addToCart
            .filter(item => (item.status !== 7 && item.status !== 2 && item.isWaiter))
            .reduce((total, item) => {
                const qty = item.quantity ?? 0;

                if (item.discountPercent) {
                    const discountPrice = calculatePriceAfterDiscount(item.priceItem ? item.priceItem : item.price, item.discountPercent);
                    return total + (discountPrice * qty);
                } else {
                    const price = Number(item.totalPriceItem ? item.totalPriceItem : item.price ?? 0);
                    return total + (price * qty);
                }
            }, 0);
    }

    const scalTotal = calculateTotalPrice(addToCart);
    const calTotalWaiter = calculateTotalPriceIsWaiter(addToCart);

    const subTotalWaiter = convertToBaseAmount(
        calTotalWaiter,
        getCurrency ? getCurrency.code : "USD",
        getCurrency?.rateToUSD
    )

    const updatedItems = listItems.map(item => ({
        ...item,
        quantity: 0
    }));

    const handleAccept = () => {
        if (tableId && shop) {
            dispatch({ type: ACTIONS.SET_LOADING_ACCEPT, payload: true });
            const filteredOrders = addToCart.filter(order => order.isCheck);

            const waiterTrue: productType[] = [];
            const waiterFalseOrUndefined: productType[] = [];

            filteredOrders.forEach(item =>
                (item.isWaiter ? waiterTrue : waiterFalseOrUndefined).push(item)
            );

            const acceptData = getFilteredData(waiterFalseOrUndefined, "default");

            console.log(acceptData, "acceptData");

            const dataObj = {
                status: productStatus.Accept,
                data: acceptData
            };

            const productOrder = getFilteredData(waiterTrue, "new");

            let dataObjNew = {
                tableId: tableId,
                businessId: shop._id,
                source: OrderSource.WAITER_POS,
                orderMode: OrderMode.DINE_IN,
                orderProducts: productOrder,
            };

            const handleApiOrderActionSuccess = (res: any) => {
                if (res.data.statusCode === 200) {
                    setAddToCart([]);
                    const result = {
                        data: res.data.data.map((item: any) => ({
                            ...item,
                            tableId: tableId,
                            businessId: shop._id,
                        })),
                    };

                    console.log(result, "result");

                    socket.emit(SocketEvent.WAITER_ACCEPT_ORDER, result);
                    finalizeOrderProcessing();
                } else {
                    if (res.data.statusCode === 401) {
                        setTimeout(() => {
                            dispatch({ type: ACTIONS.SET_ACCEPT_MODAL, payload: false });
                            dispatch({ type: ACTIONS.SET_LOADING_ACCEPT, payload: false });
                            showToastInvalid("Request order unauthorized.");
                        }, 1000)
                    } else {
                        setTimeout(() => {
                            dispatch({ type: ACTIONS.SET_ACCEPT_MODAL, payload: false });
                            dispatch({ type: ACTIONS.SET_LOADING_ACCEPT, payload: false });
                            showToastInvalid("Request order invalid.");
                        }, 1000)
                    }
                }
            };

            const finalizeOrderProcessing = () => {
                setAddToCart([]);
                setTimeout(() => {
                    dispatch({ type: ACTIONS.SET_ACCEPT_MODAL, payload: false });
                    dispatch({ type: ACTIONS.SET_LOADING_ACCEPT, payload: false });
                    resetSelectAll();
                    setListItems(updatedItems);
                    fetchProduct();
                    showToastSuccess("Accept order successfully.");
                    fetchOrderByTableId();
                }, 1000);
            };

            if (waiterFalseOrUndefined.length) {
                apiOrderAction(dataObj).then(handleApiOrderActionSuccess);
            }

            if (waiterTrue.length) {
                socket.emit(SocketEvent.WAITER_CREATE_ORDER, dataObjNew, (response: any) => {
                    if (response.success) {
                        finalizeOrderProcessing();
                        const payload = {
                            tableId,
                            colorStatus: ["support", "newOrder"],
                        };
                        socket.emit(SocketEvent.NOTIFICATION_READ, payload);
                    } else {
                        setTimeout(() => {
                            dispatch({ type: ACTIONS.SET_ACCEPT_MODAL, payload: false });
                            dispatch({ type: ACTIONS.SET_LOADING_ACCEPT, payload: false });
                            showToastInvalid("Request order invalid.");
                        }, 1000)
                    }
                });
            }
        } else {
            dispatch({ type: ACTIONS.SET_ACCEPT_MODAL, payload: false });
            dispatch({ type: ACTIONS.SET_LOADING_ACCEPT, payload: false });
            setTimeout(() => {
                showToastInvalid("Request order invalid.");
            }, 300)
        }
    };

    const handleReject = () => {
        if (tableId && shop) {
            dispatch({ type: ACTIONS.SET_LOADING_REJECT, payload: true });
            const filteredOrders = addToCart.filter(order => order.isCheck);
            const waiterFalseOrUndefined: productType[] = [];

            filteredOrders.forEach(item => {
                if (!item.isWaiter) {
                    waiterFalseOrUndefined.push(item);
                }
            });

            const acceptData = getFilteredData(waiterFalseOrUndefined, "default");

            const dataObj = {
                status: productStatus.Reject,
                data: getFilteredDataReject(acceptData)
            };

            console.log("Reject data : ", dataObj);

            if (dataObj.data.length) {
                socket.emit(SocketEvent.CHEF_ACTION_ORDER, dataObj, (response: any) => {
                    if (response.success) {
                        setAddToCart([]);
                        fetchOrderByTableId();
                        dispatch({ type: ACTIONS.SET_REJECT_MODAL, payload: false });
                        dispatch({ type: ACTIONS.SET_LOADING_REJECT, payload: false });
                        resetSelectAll();
                        setListItems(updatedItems);
                        showToast("success");
                        fetchProduct();
                    } else {
                        if (response.statusCode === 401) {
                            setTimeout(() => {
                                dispatch({ type: ACTIONS.SET_REJECT_MODAL, payload: false });
                                dispatch({ type: ACTIONS.SET_LOADING_REJECT, payload: false });
                                showToastInvalid("Request order unauthorized.");
                            }, 1000)
                        } else {
                            setTimeout(() => {
                                dispatch({ type: ACTIONS.SET_REJECT_MODAL, payload: false });
                                dispatch({ type: ACTIONS.SET_LOADING_REJECT, payload: false });
                                showToastInvalid("Request order invalid.");
                            }, 1000)
                        }
                    }
                });
            }
        } else {
            dispatch({ type: ACTIONS.SET_REJECT_MODAL, payload: false });
            dispatch({ type: ACTIONS.SET_LOADING_REJECT, payload: false });
            setTimeout(() => {
                showToastInvalid("Request order invalid.");
            }, 300)
        }
    }

    return (
        <>
            <div className="absolute bottom-0 z-20 w-full bg-white rounded-b-[10px]">
                <div className="border-t-[2px] border-dashed border-[#B6B6B6]">
                    <div className="flex flex-col gap-3 py-4 px-4">
                        <div className="flex justify-between gap-2">
                            <p className="text-total text-[#000000]">{t('total')}</p>
                            <p className="text-price-total text-danger">{formatPriceByCurrency(subTotalWaiter + scalTotal, getCurrency?.symbol)}</p>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <button
                                disabled={filteredOrdersReject.length === 0}
                                className={`py-4 rounded-[10px] text-btn bg-danger text-[#FFFFFF] flex items-center gap-2 justify-center ${filteredOrdersReject.length === 0 ? "cursor-not-allowed" : "cursor-pointer"}`}
                                onClick={() => dispatch({ type: ACTIONS.SET_REJECT_MODAL, payload: true })}
                            >
                                {t('reject')}
                            </button>
                            <button
                                disabled={filteredOrders.length === 0}
                                className={`py-4 flex items-center gap-2 justify-center text-btn bg-primary rounded-[10px] text-[#FFFFFF] ${filteredOrders.length === 0 ? "cursor-not-allowed" : "cursor-pointer"}`}
                                onClick={() => dispatch({ type: ACTIONS.SET_ACCEPT_MODAL, payload: true })}
                            >
                                {t('accept')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {state.isAccept && (
                <AcceptModal
                    isOpen={state.isAccept}
                    onConfirm={handleAccept}
                    onRequestClose={() => dispatch({ type: ACTIONS.SET_ACCEPT_MODAL, payload: false })}
                    loading={state.loadingAccept}
                    message="Are you sure?"
                    messageH="Are you sure to accept the order ?"
                />
            )}

            {state.isReject && (
                <RejectModal
                    isOpen={state.isReject}
                    onConfirm={handleReject}
                    onRequestClose={() => dispatch({ type: ACTIONS.SET_REJECT_MODAL, payload: false })}
                    loading={state.loadingReject}
                    message="Are you sure?"
                    messageH="Are you sure to reject the order ?"
                />
            )}

            <NotificationAlert />
        </>
    )
}
